import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  Grid,
  FormHelperText,
  CircularProgress
} from '@mui/material';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import { getAboutPage, updateAboutPage } from 'src/ApiServices';
import { toast } from 'react-toastify';

export default function About() {
  const [aboutData, setAboutData] = useState({
    title: '',
    description: '',
    picture: null,
    picturePreview: '',
    socialLinks: {
      facebook: '',
      x: '',
      instagram: '',
      linkedin: '',
      tiktok: ''
    }
  });

  const [errors, setErrors] = useState({
    facebook: '',
    x: '',
    instagram: '',
    linkedin: '',
    tiktok: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // Load initial data from API
  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    try {
      setLoading(true);
      const response = await getAboutPage();

      if (response.data) {
        const data = response.data;
        setAboutData({
          title: data.title || '',
          description: data.description || '',
          picture: null,
          picturePreview: data.image || '',
          socialLinks: {
            facebook: data.facebook_url || '',
            x: data.x_url || '',
            instagram: data.instagram_url || '',
            linkedin: data.linkedin_url || '',
            tiktok: data.tiktok_url || ''
          }
        });
      }
    } catch (error) {
      console.error('Error fetching about page data:', error);
      toast.error(error.response?.data?.message || 'Failed to load about page data');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const validateSocialLink = (platform, value) => {
    if (!value) return '';

    const patterns = {
      facebook: /^(https?:\/\/)?(www\.)?(facebook|fb)\.com\/.+/i,
      x: /^(https?:\/\/)?(www\.)?(twitter|x)\.com\/.+/i,
      instagram: /^(https?:\/\/)?(www\.)?instagram\.com\/.+/i,
      linkedin: /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|company)\/.+/i,
      tiktok: /^(https?:\/\/)?(www\.)?tiktok\.com\/@?.+/i
    };

    if (!patterns[platform]) {
      return `Validation not supported for ${platform}`;
    }

    if (!patterns[platform].test(value)) {
      return `Please enter a valid ${platform} URL`;
    }

    return '';
  };

  const handleInputChange = (field, value) => {
    setAboutData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSocialLinkChange = (platform, value) => {
    setAboutData((prev) => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: value
      }
    }));

    // Validate the input
    const error = validateSocialLink(platform, value);
    setErrors((prev) => ({
      ...prev,
      [platform]: error
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

      if (!allowedFormats.includes(file.type.toLowerCase())) {
        toast.error('Please upload a valid image file (JPEG, JPG, PNG, or WebP only)');
        event.target.value = '';
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setAboutData((prev) => ({
          ...prev,
          picture: file,
          picturePreview: e.target.result
        }));
      };
      reader.onerror = () => {
        toast.error('Failed to read image file');
        event.target.value = '';
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    const newErrors = {
      facebook: validateSocialLink('facebook', aboutData.socialLinks.facebook),
      x: validateSocialLink('x', aboutData.socialLinks.x),
      instagram: validateSocialLink('instagram', aboutData.socialLinks.instagram),
      linkedin: validateSocialLink('linkedin', aboutData.socialLinks.linkedin),
      tiktok: validateSocialLink('tiktok', aboutData.socialLinks.tiktok)
    };

    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((error) => error !== '');

    if (hasErrors) {
      toast.error('Please enter valid social links');
      return;
    }

    if (!aboutData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!aboutData.description.trim()) {
      toast.error('Description is required');
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('title', aboutData.title);
      formData.append('description', aboutData.description);
      formData.append('facebook_url', aboutData.socialLinks.facebook);
      formData.append('x_url', aboutData.socialLinks.x);
      formData.append('instagram_url', aboutData.socialLinks.instagram);
      formData.append('linkedin_url', aboutData.socialLinks.linkedin);
      formData.append('tiktok_url', aboutData.socialLinks.tiktok);

      if (aboutData.picture) {
        formData.append('image', aboutData.picture);
      }

      const response = await updateAboutPage(formData);

      if (response.status === 200 || response.status === 201) {
        toast.success(response?.data?.message || 'About page updated successfully!');

        await fetchAboutData();
      } else {
        throw new Error('Unexpected response status');
      }
    } catch (error) {
      console.error('Error saving about page:', error);

      if (error.response) {
        const errorMessage =
          error.response.data?.message || error.response.data?.error || 'Failed to save about page';
        toast.error(errorMessage);

        if (error.response.data?.errors) {
          const backendErrors = error.response.data.errors;
          Object.keys(backendErrors).forEach((key) => {
            toast.error(`${key}: ${backendErrors[key][0]}`);
          });
        }
      } else if (error.request) {
        toast.error('No response from server. Please check your connection.');
      } else {
        toast.error(error.message || 'Failed to save about page');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setAboutData({
      title: '',
      description: '',
      picture: null,
      picturePreview: '',
      socialLinks: {
        facebook: '',
        x: '',
        instagram: '',
        linkedin: '',
        tiktok: ''
      }
    });
    setErrors({
      facebook: '',
      x: '',
      instagram: '',
      linkedin: '',
      tiktok: ''
    });
    toast.info('Form cleared');
  };

  if (loading && initialLoad) {
    return (
      <Page title={`About | ${process.env.REACT_APP_PROJECTNAME}`}>
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
          }}
        >
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  return (
    <Page title={`About | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, mt: 2, pb: 2, pl: 1 }}>
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Manage About Page
          </Typography>
        </Box>

        <Card sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Title Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={aboutData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                variant="outlined"
                disabled={loading || saving}
                required
              />
            </Grid>

            {/* Description Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={aboutData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                variant="outlined"
                disabled={loading || saving}
                required
              />
            </Grid>

            {/* Picture Upload Field */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Picture
              </Typography>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ marginBottom: 16 }}
                disabled={loading || saving}
              />
              {aboutData.picturePreview && (
                <Box sx={{ mt: 2 }}>
                  <img
                    src={aboutData.picturePreview}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '1px solid #ddd'
                    }}
                  />
                </Box>
              )}
            </Grid>

            {/* Social Links Fields */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Social Links
              </Typography>
              <Grid container spacing={2}>
                {/* Facebook & X (Twitter) in first row */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Facebook URL"
                    value={aboutData.socialLinks.facebook}
                    onChange={(e) => handleSocialLinkChange('facebook', e.target.value)}
                    variant="outlined"
                    placeholder="https://facebook.com/yourpage"
                    error={!!errors.facebook}
                    disabled={loading || saving}
                  />
                  {errors.facebook && <FormHelperText error>{errors.facebook}</FormHelperText>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="X (Twitter) URL"
                    value={aboutData.socialLinks.x}
                    onChange={(e) => handleSocialLinkChange('x', e.target.value)}
                    variant="outlined"
                    placeholder="https://twitter.com/yourprofile"
                    error={!!errors.x}
                    disabled={loading || saving}
                  />
                  {errors.x && <FormHelperText error>{errors.x}</FormHelperText>}
                </Grid>

                {/* Instagram, LinkedIn & TikTok in second row */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Instagram URL"
                    value={aboutData.socialLinks.instagram}
                    onChange={(e) => handleSocialLinkChange('instagram', e.target.value)}
                    variant="outlined"
                    placeholder="https://instagram.com/yourprofile"
                    error={!!errors.instagram}
                    disabled={loading || saving}
                  />
                  {errors.instagram && <FormHelperText error>{errors.instagram}</FormHelperText>}
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="LinkedIn URL"
                    value={aboutData.socialLinks.linkedin}
                    onChange={(e) => handleSocialLinkChange('linkedin', e.target.value)}
                    variant="outlined"
                    placeholder="https://linkedin.com/in/yourprofile"
                    error={!!errors.linkedin}
                    disabled={loading || saving}
                  />
                  {errors.linkedin && <FormHelperText error>{errors.linkedin}</FormHelperText>}
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="TikTok URL"
                    value={aboutData.socialLinks.tiktok}
                    onChange={(e) => handleSocialLinkChange('tiktok', e.target.value)}
                    variant="outlined"
                    placeholder="https://tiktok.com/@yourprofile"
                    error={!!errors.tiktok}
                    disabled={loading || saving}
                  />
                  {errors.tiktok && <FormHelperText error>{errors.tiktok}</FormHelperText>}
                </Grid>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={handleClear}
                  sx={{ minWidth: 100 }}
                  disabled={loading || saving}
                >
                  Clear
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{ minWidth: 100 }}
                  disabled={loading || saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Box>
    </Page>
  );
}
